/**
 * NotifySupervisorModal
 *
 * Shown when the user taps "Notify my Supervisor" from a record detail modal.
 * Provides a multi-select dropdown of users with delivery department roles
 * (excluding Driver) for the current location, plus Send and Cancel buttons.
 *
 * Cancel → returns the user to the detail modal (calls onBack).
 * Send   → sends the notification and closes (calls onClose).
 *
 * When no recordId is given, createRecord saves the record on Send, so a cancelled
 * modal never creates it.
 */

import React, { useState, useEffect, useCallback } from 'react';
import {
  View,
  Text,
  Modal,
  TouchableOpacity,
  ScrollView,
  ActivityIndicator,
  Platform,
  StyleSheet,
} from 'react-native';
import MaterialIcons from 'react-native-vector-icons/MaterialIcons';
import { useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { RootState } from '../../store';
import { FontFamily } from '../../constants/fonts';
import { fetchUsersByRoleNames, CommonUserRecord, sendHarborNotification } from '../../api/deliveryRpmApi';
import { useDarkStyles, useDarkColor } from '../../theme/useDarkStyles';

// ─── Types ────────────────────────────────────────────────────────────────────

interface SupervisorUser {
  id: string;
  name: string;
  role: string;
}

interface NotifySupervisorModalProps {
  recordType: 'Haulback' | 'Roadblock';
  recordId?: number;
  createRecord?: () => Promise<number>;
  onClose: () => void;  // called after successful send or generic close
  onBack: () => void;   // called when Cancel is pressed
}

// ─── Supervisor role names to notify ────────────────────────────────────────

const SUPERVISOR_ROLE_NAMES = ['Delivery District Leader', 'Area Delivery Manager'];

// Turns "Jane.Smith@pepsico.com" into "Jane Smith"
const emailToDisplayName = (email: string): string => {
  const localPart = email?.split('@')[0] ?? '';
  return localPart
    .split(/[._]/)
    .filter(Boolean)
    .map(part => part.charAt(0).toUpperCase() + part.slice(1).toLowerCase())
    .join(' ') || email;
};

const loadSupervisors = async (location?: string): Promise<{ display: SupervisorUser[]; records: CommonUserRecord[] }> => {
  const records: CommonUserRecord[] = await fetchUsersByRoleNames(SUPERVISOR_ROLE_NAMES, location);
  const seen = new Set<string>();
  const display: SupervisorUser[] = [];
  for (const rec of records) {
    const id = rec.GPID || rec.UserEmail;
    if (!id || seen.has(id)) continue;
    seen.add(id);
    display.push({
      id,
      name: emailToDisplayName(rec.UserEmail),
      role: rec.RoleName || rec.Role || '',
    });
  }
  return { display, records };
};



// ─── Component ────────────────────────────────────────────────────────────────

const NotifySupervisorModal: React.FC<NotifySupervisorModalProps> = ({
  recordType,
  recordId,
  createRecord,
  onClose,
  onBack,
}) => {
  const dc = useDarkColor();
  const ns = useDarkStyles(baseNs);
  const { t } = useTranslation('harbor');
  const selectedLocation = useSelector((state: RootState) => state.appData?.selectedLocation);
  const [users, setUsers] = useState<SupervisorUser[]>([]);
  const [userRecords, setUserRecords] = useState<CommonUserRecord[]>([]);
  const [loadingUsers, setLoadingUsers] = useState(true);
  const [selected, setSelected] = useState<string[]>([]);
  const [sending, setSending] = useState(false);
  const [sendError, setSendError] = useState('');

  // Translate role name based on current language
  const translateRole = (role: string): string => {
    const translationKey = `notify_supervisor_modal.roles.${role}`;
    const translated = t(translationKey);
    // If translation key doesn't exist, fallback to original role name
    return translated !== translationKey ? translated : role;
  };

  useEffect(() => {
    let cancelled = false;
    setLoadingUsers(true);
    loadSupervisors(selectedLocation?.name)
      .then(({ display, records }) => {
        if (!cancelled) {
          setUsers(display);
          setUserRecords(records);
        }
      })
      .catch(() => {
        if (!cancelled) {
          setUsers([]);
          setUserRecords([]);
        }
      })
      .finally(() => { if (!cancelled) setLoadingUsers(false); });
    return () => { cancelled = true; };
  }, [selectedLocation?.name]);

  const toggleUser = useCallback((id: string) => {
    setSelected(prev =>
      prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]
    );
  }, []);

  const handleSend = async () => {
    if (selected.length === 0) return;
    setSending(true);
    setSendError('');
    try {
      const id = recordId ?? (createRecord ? await createRecord() : undefined);
      if (id === undefined) throw new Error('No record to notify about');
      await sendHarborNotification(recordType, id, userRecords, selected);
      onClose();
    } catch (err: any) {
      const errorMessage = err?.response?.data?.message 
        || err?.message 
        || 'Failed to send notification. Please try again.';
      setSendError(errorMessage);
    } finally {
      setSending(false);
    }
  };

  return (
    <Modal visible transparent animationType="fade" onRequestClose={onBack}>
      <View style={ns.overlay}>
        <View style={ns.sheet}>
          {/* Header */}
          <View style={ns.header}>
            <Text style={ns.title}>{t('notify_supervisor_modal.title')}</Text>
            <TouchableOpacity style={ns.closeBtn} onPress={onBack} accessibilityLabel={t('notify_supervisor_modal.action_close')}>
              <MaterialIcons name="close" size={18} color={dc("#616161")} />
            </TouchableOpacity>
          </View>

          {/* Body */}
          <View style={ns.body}>
            <Text style={ns.instruction}>
              {t('notify_supervisor_modal.instruction', { recordType: recordType.toLowerCase() })}
            </Text>

            {loadingUsers ? (
              <View style={ns.loadingWrap}>
                <ActivityIndicator size="small" color={dc("#005CBC")} />
              </View>
            ) : users.length === 0 ? (
              <Text style={ns.emptyText}>{t('notify_supervisor_modal.error_no_supervisors_found')}</Text>
            ) : (
              <ScrollView style={ns.listScroll} contentContainerStyle={ns.list}>
                {users.map(u => {
                  const checked = selected.includes(u.id);
                  return (
                    <TouchableOpacity
                      key={u.id}
                      style={[ns.userRow, checked && ns.userRowSelected]}
                      onPress={() => toggleUser(u.id)}
                      accessibilityLabel={`${checked ? t('notify_supervisor_modal.a11y_deselect') : t('notify_supervisor_modal.a11y_select')} ${u.name}`}
                    >
                      <View style={[ns.checkbox, checked && ns.checkboxChecked]}>
                        {checked && <MaterialIcons name="check" size={12} color="#fff" />}
                      </View>
                      <View style={ns.userInfo}>
                        <Text style={ns.userName}>{u.name}</Text>
                        <Text style={ns.userRole}>{translateRole(u.role)}</Text>
                      </View>
                    </TouchableOpacity>
                  );
                })}
              </ScrollView>
            )}

            {!!sendError && (
              <Text style={ns.errorText}>{t('notify_supervisor_modal.error_send_failed')}</Text>
            )}
          </View>

          {/* Footer */}
          <View style={ns.footer}>
            <TouchableOpacity style={ns.cancelBtn} onPress={onBack} accessibilityLabel={t('notify_supervisor_modal.action_cancel')}>
              <Text style={ns.cancelBtnText}>{t('notify_supervisor_modal.action_cancel')}</Text>
            </TouchableOpacity>
            <TouchableOpacity
              style={[ns.sendBtn, (selected.length === 0 || sending) && ns.sendBtnDisabled]}
              onPress={handleSend}
              disabled={selected.length === 0 || sending}
              accessibilityLabel={t('notify_supervisor_modal.a11y_send_notification')}
            >
              {sending
                ? <ActivityIndicator size="small" color="#fff" />
                : <Text style={ns.sendBtnText}>{t('notify_supervisor_modal.action_send')}</Text>
              }
            </TouchableOpacity>
          </View>
        </View>
      </View>
    </Modal>
  );
};

export default NotifySupervisorModal;

// ─── Styles ───────────────────────────────────────────────────────────────────

const baseNs = StyleSheet.create({
  overlay: {
    flex: 1, backgroundColor: 'rgba(0,0,0,0.6)',
    alignItems: 'center', justifyContent: 'center', padding: 20,
  },
  sheet: {
    backgroundColor: '#fff', borderRadius: 10,
    width: '100%', maxWidth: 440, maxHeight: '80%', overflow: 'hidden',
    ...Platform.select({
      ios: { shadowColor: '#000', shadowOffset: { width: 0, height: 8 }, shadowOpacity: 0.2, shadowRadius: 24 },
      android: { elevation: 8 },
    }),
  },
  header: {
    flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between',
    paddingHorizontal: 24, paddingVertical: 16, borderBottomWidth: 1, borderBottomColor: '#EBEBEB',
  },
  title: { fontSize: 18, fontWeight: '700', color: '#1A1A1A', fontFamily: FontFamily.bold },
  closeBtn: {
    width: 32, height: 32, borderRadius: 6,
    alignItems: 'center', justifyContent: 'center', backgroundColor: '#F5F5F5',
  },
  body: { padding: 20, gap: 14 },
  instruction: { fontSize: 14, color: '#616161', fontFamily: FontFamily.regular, lineHeight: 20 },
  loadingWrap: { paddingVertical: 24, alignItems: 'center' },
  emptyText: { fontSize: 13, color: '#9E9E9E', fontFamily: FontFamily.regular, fontStyle: 'italic' },
  listScroll: { maxHeight: 260 },
  list: { gap: 4 },
  userRow: {
    flexDirection: 'row', alignItems: 'center', gap: 12,
    paddingVertical: 10, paddingHorizontal: 12, borderRadius: 8,
    borderWidth: 1, borderColor: '#EBEBEB', backgroundColor: '#fff',
  },
  userRowSelected: { borderColor: '#034895', backgroundColor: '#EFF5FC' },
  checkbox: {
    width: 20, height: 20, borderRadius: 4,
    borderWidth: 2, borderColor: '#BDBDBD', backgroundColor: '#fff',
    alignItems: 'center', justifyContent: 'center',
  },
  checkboxChecked: { backgroundColor: '#034895', borderColor: '#034895' },
  userInfo: { flex: 1 },
  userName: { fontSize: 14, fontWeight: '600', color: '#1A1A1A', fontFamily: FontFamily.medium },
  userRole: { fontSize: 12, color: '#9E9E9E', fontFamily: FontFamily.regular },
  errorText: { fontSize: 13, color: '#C12C01', fontFamily: FontFamily.regular },
  footer: {
    flexDirection: 'row', justifyContent: 'flex-end', gap: 12,
    paddingHorizontal: 24, paddingVertical: 16, borderTopWidth: 1, borderTopColor: '#EBEBEB',
  },
  cancelBtn: {
    height: 38, paddingHorizontal: 24, borderRadius: 24,
    borderWidth: 1, borderColor: '#034895', alignItems: 'center', justifyContent: 'center',
  },
  cancelBtnText: { fontSize: 14, fontWeight: '700', color: '#034895', fontFamily: FontFamily.bold },
  sendBtn: {
    height: 38, paddingHorizontal: 28, borderRadius: 24,
    backgroundColor: '#034895', alignItems: 'center', justifyContent: 'center', minWidth: 80,
  },
  sendBtnDisabled: { backgroundColor: '#B0B0B0' },
  sendBtnText: { fontSize: 14, fontWeight: '700', color: '#fff', fontFamily: FontFamily.bold },
});
